import React, {useEffect} from 'react';
import {IStorefrontLink, IStorefrontSettings} from '../../Api/Models/StorefrontSettings';
import CartIcon from '../Cart/CartIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Menu,
    MenuButton, MenuDivider, MenuGroup, MenuGroupHeader, MenuItem, MenuItemLink, MenuList,
    MenuPopover,
    MenuTrigger,
    Persona
} from '@fluentui/react-components';
import { SK_FIRST_NAME, SK_LAST_NAME } from '../../Constants/LocalStorageKeys';
import CAN, {ORDERS} from "../../Components/Auth/Permissions";
import LoginPopover from "../Auth/LoginPopover";
import SearchBox from "../Search/SearchBox";
import { parseAutocompleteConfig } from "../Search/autocompleteConfig";
import { useFavorites } from "../Helpers/useFavorites";
import {groupLinksBySections} from "./Footer";
import ScrollToTop from "../ScrollToTop";
import {isDarkColor, readableOn} from "../Helpers/ColorHelpers";
import {IHeaderLink, isExternalUrl, isNonNavigableUrl, parseHeaderLinksConfig, headerLinksJustify} from "../Helpers/headerLinksConfig";
import {elementStyleFrom} from "../Helpers/searchPageConfig";
import {parseHeaderConfig} from "../Helpers/headerFooterConfig";
import {parseLayout} from "../Helpers/layout";
import LayoutRenderer from "../Layout/LayoutRenderer";
import {parseCartPageConfig} from "../Helpers/cartPageConfig";
import CartPanel from "../Cart/CartPanel";
import HeaderDropdown, { HeaderMenuAccordionItem } from "./HeaderDropdown";
import {useLocalization} from "../Helpers/useLocalization";
import {useIsNarrow} from "../Helpers/useIsNarrow";
import NotificationBars, {hasVisibleNotificationBars} from "./NotificationBars";

interface INavBarProps {
    settings: IStorefrontSettings;
    // Home-route "hero under transparent header" mode (Layout decides when this is on — the
    // merchant opted in, the full-bleed banner is on, we're on desktop, and there's a banner to
    // render). The header floats transparently over the hero and turns solid on scroll.
    underHeaderHero?: boolean;
    // Optional chrome for the floating bar's not-scrolled state (homePageConfig
    // .bannerUnderHeaderBackground / -Shadow): a tint (usually rgba, for text legibility over a
    // busy hero) and a box-shadow. Only read while underHeaderHero is on and the shopper hasn't
    // scrolled; unset keeps the bar fully transparent / shadowless.
    underHeaderBarBackground?: string;
    underHeaderBarShadow?: string;
}

const NavBar: React.FC<INavBarProps> = (props) => {
    const [headerLinkChunks, setHeaderLinkChunks] = React.useState<IStorefrontLink[][]>([]);
    const [keywords, setKeywords] = React.useState<string>('');
    const canSeeOrders = CAN(ORDERS);
    const { enabled: favoritesEnabled } = useFavorites();
    const name = localStorage.getItem(SK_FIRST_NAME) != null || localStorage.getItem(SK_LAST_NAME) != null
        ? (localStorage.getItem(SK_FIRST_NAME) ?? '') + ' ' + (localStorage.getItem(SK_LAST_NAME) ?? '')
        : undefined;

    useEffect(() => {
        if (Array.isArray(props.settings.storefrontMenuLinks)) {
            setHeaderLinkChunks(groupLinksBySections(props.settings.storefrontMenuLinks));
        }
    }, [props.settings.storefrontMenuLinks]);
    

    const nav = useNavigate();
    const L = useLocalization();
    // Below this width the header switches to its stacked mobile layout and the custom links
    // collapse into a menu (they otherwise wrap to several lines). When the storefront already
    // has a menu button (storefrontMenuLinks), we fold the links into that menu rather than
    // adding a second button; only when there's no existing menu do we show a standalone "More".
    const isNarrow = useIsNarrow(700);
    const hasMenuLinks = !!(props.settings.storefrontMenuLinks && props.settings.storefrontMenuLinks.length > 0);

    // "Hero under transparent header" mode: while the shopper is at the top of the hero the header
    // is transparent; once they scroll past it turns solid. Only wire the listener when the mode is
    // on (Layout gates it to the opted-in home route), so it's inert everywhere else.
    const [scrolled, setScrolled] = React.useState(false);
    useEffect(() => {
        if (!props.underHeaderHero) return;
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [props.underHeaderHero]);

    // Custom header links: shown centered in the header's empty space, spreading out from the center.
    const headerLinksConfig = React.useMemo(() => parseHeaderLinksConfig(props.settings.headerLinksConfig), [props.settings.headerLinksConfig]);
    const headerLinks = (headerLinksConfig.links ?? []).filter(l => (l.label && l.label.trim()) || l.iconUrl);

    // Merchant-configured header chrome styling (headerConfig JSON). Each section is applied
    // as an inline style over the element's defaults.
    const headerConfig = React.useMemo(() => parseHeaderConfig(props.settings.headerConfig), [props.settings.headerConfig]);
    // The account persona's configurable look. One set of parts drives both the signed-out
    // (LoginPopover) and signed-in (account menu) triggers, so the control keeps one identity.
    // Null when nothing is configured, so the default typography (and the golden render) is untouched.
    const accountNameCssRaw = elementStyleFrom(headerConfig.accountName);
    const accountNameCss = Object.keys(accountNameCssRaw).length ? accountNameCssRaw : undefined;
    const accountPersonaProps = {
        triggerStyle: headerConfig.account,
        nameStyle: headerConfig.accountName,
        subtextStyle: headerConfig.accountSubtext,
        avatarStyle: headerConfig.accountAvatar,
        panelStyle: headerConfig.accountPanel,
    };
    // AC-970 — header search placement + right-side action links.
    const searchRow = !!headerConfig.searchRow;                                   // search as a full-width row below the bar
    const searchRowSticky = !!headerConfig.searchRowSticky;                       // that row sticks independently of header sticky
    const searchInline = props.settings.useHeaderSearch && !searchRow;           // legacy inline-in-actions search
    const actionLinks = (headerLinksConfig.actionLinks ?? []).filter(l => (l.label && l.label.trim()) || l.iconUrl);
    // Opt-in: headerConfig.hideAutoMenu suppresses the auto-generated "Menu" dropdown fed by
    // storefrontMenuLinks (e.g. when a storefront already surfaces those links via the inline
    // headerLinksConfig nav and the dropdown would just duplicate them). Absent/false renders
    // exactly as today. Treating this as "no menu links" (rather than only hiding the dropdown)
    // lets the narrow-screen fallback below pick the custom header links up into their own
    // standalone "More" button instead of losing their narrow-nav home.
    const showAutoMenu = hasMenuLinks && !headerConfig.hideAutoMenu;
    // Opt-in: headerConfig.brandInline moves the logo+title from the far-left brand slot into
    // the centered header-links group, as its first item (logo + Home + About + ... all centered
    // together). It only takes effect when there's an actual centered group to join — desktop
    // width (the mobile layout stacks title/links/actions into their own rows regardless) and at
    // least one header link — so the brand always has somewhere to render. Absent/false (or no
    // links to join) leaves the brand exactly where it renders today.
    const brandInline = !!headerConfig.brandInline && !isNarrow && headerLinks.length > 0;
    // When the merchant enables cartPageConfig.openInPanel, the header cart icon opens a slide-out
    // cart panel (owned here so it can overlay any page) instead of navigating to /cart.
    const cartConfig = React.useMemo(() => parseCartPageConfig(props.settings.cartPageConfig), [props.settings.cartPageConfig]);
    const openCartInPanel = !!cartConfig.openInPanel;
    const [cartPanelOpen, setCartPanelOpen] = React.useState(false);
    // Close the slide-out cart panel on any route change so it doesn't overlay the next page.
    // In particular, clicking Checkout in the panel navigates to /checkoutPrerequisites, and the
    // panel should close as the Order Prerequisites page opens.
    const location = useLocation();
    React.useEffect(() => { setCartPanelOpen(false); }, [location.pathname]);
    const headerBarStyle = React.useMemo(() => elementStyleFrom(headerConfig.bar), [headerConfig.bar]);
    // The bar's effective background: a solid headerConfig.bar color wins over menuBarColor
    // (gradients aren't parseable, so they fall through and the merchant's text color is trusted).
    const menuBarBg = (headerBarStyle.backgroundColor as string | undefined) ?? props.settings.menuBarColor;
    // Choose the menu button's light/dark base coloring from the bar's EFFECTIVE background
    // (headerConfig.bar wins over menuBarColor) so it isn't rendered dark-on-dark when the two differ.
    const isDark = isDarkColor(menuBarBg || '#fff');
    // Luminance-aware cart icon color so it never renders dark-on-dark (or light-on-light).
    const cartIconColor = React.useMemo(
        () => readableOn(menuBarBg, props.settings.menuBarTextColor),
        [menuBarBg, props.settings.menuBarTextColor]);
    const headerTitleStyle = React.useMemo(() => elementStyleFrom(headerConfig.title, 'heading'), [headerConfig.title]);
    const headerLogoStyle = React.useMemo(() => elementStyleFrom(headerConfig.logo), [headerConfig.logo]);
    const headerBrandStyle = React.useMemo(() => elementStyleFrom(headerConfig.brand), [headerConfig.brand]);
    // Merchant override for the text beside the logo; '' shows the logo alone (e.g. when the
    // logo is a wordmark that already spells the store name).
    const headerTitleText = typeof headerConfig.titleText === 'string' ? headerConfig.titleText : props.settings?.name;
    const headerSearchStyle = React.useMemo(() => elementStyleFrom(headerConfig.searchBar, 'input'), [headerConfig.searchBar]);
    // The per-storefront theme re-skins every .fui-Input with an !important translucent tint, which
    // would swallow a merchant-configured header-search background/border. We surface the configured
    // values as CSS vars so a higher-specificity index.css rule can win; unset vars fall back to the
    // theme default. (Only background/border-color are forced by the theme; width/style/radius apply
    // inline already.)
    const headerSearchVars = React.useMemo(() => {
        const vars: Record<string, string> = {};
        const bg = headerSearchStyle.backgroundColor as string | undefined;
        const borderColor = headerSearchStyle.borderColor as string | undefined;
        if (bg) vars['--sf-search-bg'] = bg;
        if (borderColor) vars['--sf-search-border'] = borderColor;
        return vars;
    }, [headerSearchStyle]);
    const headerMenuButtonStyle = React.useMemo(() => elementStyleFrom(headerConfig.menuButton), [headerConfig.menuButton]);
    const headerMenuStyle = React.useMemo(() => elementStyleFrom(headerConfig.menu), [headerConfig.menu]);
    const headerMenuHeaderStyle = React.useMemo(() => elementStyleFrom(headerConfig.menuHeader), [headerConfig.menuHeader]);
    const headerMenuItemStyle = React.useMemo(() => elementStyleFrom(headerConfig.menuItem), [headerConfig.menuItem]);
    // The built-in sf-menu-btn helper class uses !important (to beat Fluent's atomic styles), which
    // would otherwise override the merchant's configured button colors. When the merchant supplies
    // menuButton styling, drop the helper class so their config wins; otherwise keep the default.
    const menuButtonConfigured = !!headerConfig.menuButton && Object.keys(headerConfig.menuButton).length > 0;
    // The dropdown popover: its background is the merchant's menu config, else a luminance-aware
    // default (dark on a dark bar). The .sf-menu-popover class makes Fluent's opaque surfaces
    // transparent so this background shows through; item/header text colors are then applied inline.
    const menuBg = (headerMenuStyle.backgroundColor as string | undefined) || (isDark ? 'rgba(40, 42, 54, 0.97)' : '#ffffff');
    const menuBorderDefault = isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)';
    // Menu entries: configured color wins, else a legible black/white chosen from the popover's
    // luminance (so unstyled entries are never dark-on-dark on a dark popover).
    const menuItemColor = (headerMenuItemStyle.color as string | undefined) || readableOn(menuBg);
    const menuHeaderColor = (headerMenuHeaderStyle.color as string | undefined) || readableOn(menuBg);

    // The dropdown a custom header link with `children` opens. Fluent's popover surface is white, but its
    // entries take menuItemColor — derived from the header's luminance — so on a dark header the items
    // rendered light-on-white and were invisible. Give it the same surface as the built-in category menu,
    // with an explicit headerLinksConfig.dropdown override on top for stores that want the two to differ.
    const linkMenuCfg = React.useMemo(() => elementStyleFrom(headerLinksConfig.dropdown), [headerLinksConfig.dropdown]);
    const linkMenuBg = (linkMenuCfg.backgroundColor as string | undefined) || menuBg;
    const linkMenuStyle: React.CSSProperties = { backgroundColor: linkMenuBg, border: menuBorderDefault, ...headerMenuStyle, ...linkMenuCfg };
    // Entries follow whichever surface actually won, so an overridden dropdown background stays legible.
    const linkMenuItemColor = (headerMenuItemStyle.color as string | undefined) || readableOn(linkMenuBg);

    // AC-972 — the mega-menu panel surface (full-width bar + popover mega). Its background was hard-coded
    // white; instead default it luminance-aware from the header (dark panel + light text on a dark bar),
    // with an explicit `headerConfig.megaPanel` style override winning. Passed to every HeaderDropdown.
    const megaPanelCfg = React.useMemo(() => elementStyleFrom(headerConfig.megaPanel), [headerConfig.megaPanel]);
    const megaPanelBg = (megaPanelCfg.background as string | undefined) || (megaPanelCfg.backgroundColor as string | undefined) || (isDark ? 'rgb(28, 30, 40)' : '#ffffff');
    const megaPanelStyle: React.CSSProperties = {
        background: megaPanelBg,
        color: readableOn(megaPanelBg),
        borderTop: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e6ebf2',
        boxShadow: isDark ? '0 18px 40px rgba(0,0,0,.45)' : '0 18px 40px rgba(16,46,85,.12)',
        ...megaPanelCfg,
    };

    // The links center in the header's empty space when they fit, and drop to their own full-width
    // row when they don't — measured from actual content widths (so more/longer links wrap sooner)
    // rather than a fixed breakpoint.
    // navRef is on the header STACK wrapper (nav + optional search row), not the <nav> itself, so
    // the published --sf-navbar-height and the stack/unstack measurement both account for the
    // full header block (AC-970). The wrapper carries the absolute/fixed positioning; the <nav>
    // sits statically inside it.
    const navRef = React.useRef<HTMLDivElement>(null);
    const [linksStacked, setLinksStacked] = React.useState(false);
    React.useLayoutEffect(() => {
        const navEl = navRef.current;
        if (!navEl || headerLinks.length === 0) {
            setLinksStacked(false);
            return;
        }
        let frame = 0;
        // Intrinsic content span of a section: distance from its first child's left edge to its
        // last child's right edge. Unlike scrollWidth (which is clamped to clientWidth and so
        // balloons when a flex box grows to fill its row), this stays constant no matter which
        // row the section is on or how far it stretched — so the stack/unstack decision is a
        // stable fixed point that reverses cleanly when the window grows back.
        const contentSpan = (el: HTMLElement): number => {
            const kids = el.children;
            if (kids.length === 0) return 0;
            let left = Infinity;
            let right = -Infinity;
            for (let i = 0; i < kids.length; i++) {
                const r = (kids[i] as HTMLElement).getBoundingClientRect();
                if (r.width === 0 && r.height === 0) continue; // skip un-rendered nodes
                left = Math.min(left, r.left);
                right = Math.max(right, r.right);
            }
            return right > left ? right - left : 0;
        };
        const measure = () => {
            const title = navEl.querySelector('.header-title') as HTMLElement | null;
            const center = navEl.querySelector('.header-center') as HTMLElement | null;
            const actions = navEl.querySelector('.header-actions') as HTMLElement | null;
            if (!title || !center || !actions) return;
            const needed = contentSpan(title) + contentSpan(center) + contentSpan(actions);
            const SECTION_GAP = 48; // breathing room between the three sections
            setLinksStacked(needed + SECTION_GAP > navEl.clientWidth);
        };
        const onResize = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };
        measure();
        const ro = new ResizeObserver(onResize);
        ro.observe(navEl);
        return () => { cancelAnimationFrame(frame); ro.disconnect(); };
    }, [headerLinks.length, props.settings.headerLinksConfig]);

    // Publish the navbar's rendered height as --sf-navbar-height so #content can offset by it
    // (see index.css). The header is fixed/absolute and its height changes with the stacked-
    // links row, the mobile column layout, and font/config — so measure it live.
    React.useLayoutEffect(() => {
        const navEl = navRef.current;
        if (!navEl) return;
        const apply = () => document.documentElement.style.setProperty('--sf-navbar-height', `${navEl.offsetHeight}px`);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(navEl);
        return () => { ro.disconnect(); };
    }, []);

    // AC-970 — independent search-row stickiness. When the search row is set to stick but the header
    // itself is NOT sticky, the header scrolls away while the search row pins to the top. We keep the
    // row in the (absolute) header stack for its resting position, then flip just the row to
    // position:fixed once the page has scrolled past the nav bar, leaving an equal-height spacer so
    // the stack's measured height (→ --sf-navbar-height → #content offset) doesn't jump.
    const independentStickyRow = searchRow && searchRowSticky && !headerConfig.sticky && props.settings.useHeaderSearch;
    const barRef = React.useRef<HTMLElement>(null);          // the <nav> bar (pin threshold = its height)
    const searchRowRef = React.useRef<HTMLDivElement>(null); // the search row (spacer height)
    const [rowPinned, setRowPinned] = React.useState(false);
    const [rowHeight, setRowHeight] = React.useState(0);
    React.useEffect(() => {
        if (!independentStickyRow) { setRowPinned(false); return; }
        const onScroll = () => setRowPinned(window.scrollY >= (barRef.current?.offsetHeight ?? 0));
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
    }, [independentStickyRow]);
    React.useLayoutEffect(() => {
        const el = searchRowRef.current;
        if (!el) { setRowHeight(0); return; }
        const apply = () => setRowHeight(el.offsetHeight);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => ro.disconnect();
    }, [searchRow, props.settings.useHeaderSearch]);

    // Are there notification bars to show right now? Decided ONCE per config rather than per render:
    // dismissing the last bar must not flip the header from the stack layout back to the bare <nav>
    // mid-interaction, because the two are positioned by different owners and the swap would jump the
    // page. An empty stack wrapper measures the same height as the bare nav, so keeping it costs nothing.
    // Must sit up here with the other hooks — the header-layout gate below returns early.
    const showNotifications = React.useMemo(
        () => hasVisibleNotificationBars(props.settings.notificationsConfig), [props.settings.notificationsConfig]);

    // Header render context for mega-menu panels (AC-960 / S5.1): a paneled link renders its ILayout
    // via LayoutRenderer inside the flyout. No slots — a panel holds links/text/images, not the
    // search/cart/account host elements.
    const headerCtx = { settings: props.settings, region: 'header' as const, nav: (url: string) => nav(url), isNarrow, L };

    const renderHeaderLink = (link: IHeaderLink, i: number) => {
        const style = { ...elementStyleFrom(headerLinksConfig.cellStyle), ...elementStyleFrom(link.style) };
        // A link with children renders as a dropdown (AC-941 / S1.2); a plain link renders exactly
        // as today, so non-nested links (every current store) are byte-identical. A valid `panel`
        // opens a mega-menu flyout and supersedes a plain children dropdown (AC-960 / S5.1).
        const childLinks = (link.children ?? []).filter(c => (c.label && c.label.trim()) || c.iconUrl);
        const panelLayout = parseLayout(link.panel);
        if (childLinks.length > 0 || panelLayout) {
            const panelNode = panelLayout ? <LayoutRenderer layout={panelLayout} ctx={headerCtx} /> : undefined;
            return (
                <HeaderDropdown
                    key={link.id ?? i}
                    link={link}
                    onNavigate={(url) => nav(url)}
                    trigger={link.trigger}
                    triggerClassName='hc-link'
                    triggerStyle={style}
                    itemClassName='hc-menu-item'
                    itemStyle={{ color: linkMenuItemColor, ...headerMenuItemStyle }}
                    menuClassName='sf-menu-popover'
                    menuStyle={linkMenuStyle}
                    panel={panelNode}
                    panelStyle={megaPanelStyle}
                />
            );
        }
        const inner = (
            <>
                {link.iconUrl && <img src={link.iconUrl} alt="" className="hc-link-icon" />}
                {link.label && <span>{link.label}</span>}
            </>
        );
        const external = isExternalUrl(link.url);
        const nonNavigable = isNonNavigableUrl(link.url);
        // External links (and any new-tab link) are plain anchors; internal same-tab links use the router
        // so navigation stays within the SPA (no full reload). tel:/mailto: are external by the scheme
        // test above (so they already get a plain <a>, never SPA-routed) and additionally never honor
        // newTab — see isNonNavigableUrl.
        if (external || link.newTab) {
            const openInNewTab = link.newTab && !nonNavigable;
            return (
                <a key={link.id ?? i} className='hc-link' style={style} href={link.url || '#'}
                   target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                    {inner}
                </a>
            );
        }
        return (
            <a key={link.id ?? i} className='hc-link' style={style} href={link.url || '#'}
               onClick={e => { e.preventDefault(); if (link.url) nav(link.url); }}>
                {inner}
            </a>
        );
    };

    // Same header link, rendered as a dropdown entry for the collapsed "More" menu on mobile.
    const renderHeaderLinkMenuItem = (link: IHeaderLink, i: number) => {
        // A nested link folds into a tap-to-expand accordion in the collapsed/narrow menu (AC-942 /
        // S1.3), since there's no hover on touch; a plain link renders as a single menu entry.
        const childLinks = (link.children ?? []).filter(c => (c.label && c.label.trim()) || c.iconUrl);
        const panelLayout = parseLayout(link.panel);
        if (childLinks.length > 0 || panelLayout) {
            // On narrow the panel stacks (headerCtx.isNarrow is true here), shown inline in the accordion.
            const panelNode = panelLayout ? <LayoutRenderer layout={panelLayout} ctx={headerCtx} /> : undefined;
            return (
                <HeaderMenuAccordionItem
                    key={link.id ?? i}
                    link={link}
                    onNavigate={(url) => nav(url)}
                    itemClassName='hc-menu-item'
                    itemStyle={{ color: menuItemColor, ...headerMenuItemStyle }}
                    panel={panelNode}
                />
            );
        }
        // The .hc-link-icon sizing is scoped to .header-center, so in the popover the icon must be
        // sized inline — otherwise the pip renders at its full natural size and crowds out the label.
        const content = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', whiteSpace: 'nowrap' }}>
                {link.iconUrl && <img src={link.iconUrl} alt="" style={{ height: '1.2em', width: 'auto', maxWidth: '1.5em', objectFit: 'contain', flex: '0 0 auto' }} />}
                {link.label && <span>{link.label}</span>}
            </span>
        );
        const external = isExternalUrl(link.url);
        const nonNavigable = isNonNavigableUrl(link.url);
        if (external || link.newTab) {
            const openInNewTab = link.newTab && !nonNavigable;
            return (
                <MenuItemLink key={link.id ?? i} className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle }}
                    href={link.url || '#'} target={openInNewTab ? '_blank' : undefined}
                    rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                    {content}
                </MenuItemLink>
            );
        }
        return (
            <MenuItem key={link.id ?? i} className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle }}
                onClick={() => { if (link.url) nav(link.url); }}>
                {content}
            </MenuItem>
        );
    };

    // The logo + title text, shared between the left-pinned brand slot and the centered
    // header-links group — see brandInline above for which slot it renders into.
    const renderBrand = () => (
        <>
            {props.settings.headerLogo &&
                <img src={props.settings.headerLogo} alt="Logo" style={{ height: '3em', cursor: 'pointer', ...headerLogoStyle }} onClick={goToHeaderLink} />
            }
            {headerTitleText &&
            <div style={{ marginLeft: props.settings.headerLogo ? '0.5em' : 0, fontSize: 'clamp(0.85rem, 2.5vw, 2em)', cursor: 'pointer', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, ...headerTitleStyle }} onClick={goHome}>
                {headerTitleText}
            </div>
            }
        </>
    );

    const goHome = () => {
        nav(`/`);
    }

    const goToHeaderLink = () => {
        const link = props.settings.headerLink ?? '/';
        if (link.startsWith('http')) {
            window.open(link, '_blank');
        }
        else{
            nav(link);
        }
    }

    // The header bar's own autocomplete config. The home page hero bar and the search page bar read
    // their own blocks, so all three are enabled, scoped and styled independently.
    const headerAutocomplete = React.useMemo(
        () => parseAutocompleteConfig(props.settings.headerConfig), [props.settings.headerConfig]);

    // Takes the value rather than reading `keywords`: SearchBox reports the text it submitted, which
    // may be a suggestion the shopper picked rather than what they had typed.
    const searchFor = (value: string) => {
        nav(`/search?keywords=${encodeURIComponent(value)}`);
    }

    const goToAccount = () => {
        nav(`/account`);
    }

    // Follow an internal menu link that points at the page we're already on without a full reload:
    // React Router navigate() swaps the URL (and its query params) in place, so a link like
    // /home?warp=3 while already on /home just changes ?warp=N instead of hard-reloading — keeping
    // the page and its animated background alive. Links to a different page keep the normal (full)
    // navigation, and external links / new-tab (modified) clicks fall through to the browser.
    const handleMenuLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url?: string) => {
        if (!url || isExternalUrl(url)) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        let samePage = false;
        try { samePage = new URL(url, window.location.origin).pathname === location.pathname; } catch { /* keep default */ }
        if (!samePage) return;
        e.preventDefault();
        nav(url);
    };

    const getLinkElement = (link: IStorefrontLink, index: number)=> {
        if(link.isSeparator){
            return (

                <MenuItem key={`nav-link-separator-${index}`}>

                        <MenuGroupHeader style={{ color: menuHeaderColor, ...headerMenuHeaderStyle, ...(link.color ? { color: link.color } : {}) }} onClick={() => {
                            if (link.url) {
                                if (link.url.startsWith('http')) {
                                    window.open(link.url, '_blank');
                                } else {
                                    nav(link.url);
                                }
                            }
                        }}>
                            {link.text}
                        </MenuGroupHeader>
                </MenuItem>

            );
        }
        return (
            <MenuItemLink key={`nav-link-${index}`} className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle, ...(link.color ? { color: link.color } : {}) }} href={link.url ?? ''} onClick={(e) => handleMenuLinkClick(e, link.url ?? undefined)}>{link.text}</MenuItemLink>
        );
    }
    

    // Hero-under-header overrides, applied AFTER headerBarStyle so the transparent state wins over
    // the merchant's bar config. backgroundImage is deliberately left untouched so a merchant can
    // layer a scrim gradient (headerConfig.bar) behind the floating header. zIndex:200 is applied in
    // both absolute and fixed modes so the header paints above the hero's position:absolute overlay
    // layers, and a transition animates the solid-on-scroll change.
    const underHeaderHeroStyle: React.CSSProperties = props.underHeaderHero
        ? {
            zIndex: 200,
            transition: 'background-color 200ms ease, box-shadow 200ms ease',
            ...(!scrolled ? {
                backgroundColor: props.underHeaderBarBackground ?? 'transparent',
                boxShadow: props.underHeaderBarShadow ?? 'none',
            } : {}),
        }
        : {};

    // AC-947 / S2.4 — header layout gate: a valid, non-empty `layout` routes to the grid renderer;
    // otherwise fall through to the legacy header below. Non-adopters (no layout) are byte-identical
    // (enforced by the golden-render test, AC-935).
    const headerLayout = parseLayout(headerConfig.layout);
    if (headerLayout) {
        const searchSlot = props.settings.useHeaderSearch ? (
            <div className='home-search-bar'>
                <SearchBox
                    style={{ margin: '0 auto', width: '100%', ...headerSearchStyle, ...headerSearchVars }}
                    placeholder={L('localization_nav_search_placeholder')}
                    value={keywords}
                    onValueChange={setKeywords}
                    onSearch={searchFor}
                    suggestionsAvailable={props.settings.enableSearchAutocomplete}
                    autocomplete={headerAutocomplete}
                />
            </div>
        ) : undefined;
        const layoutNav = (
            <nav id='navbar' className='sf-layout-nav' style={{ backgroundColor: props.settings.menuBarColor, color: props.settings.menuBarTextColor, fontFamily: props.settings.headerFont, ...headerBarStyle, ...(showNotifications ? { position: 'static' as const } : headerConfig.sticky ? { position: 'fixed' as const, top: 0, left: 0, right: 0, zIndex: 200 } : {}) }}>
                <LayoutRenderer
                    layout={headerLayout}
                    ctx={{
                        settings: props.settings,
                        region: 'header',
                        nav: (url) => nav(url),
                        isNarrow,
                        L,
                        slots: {
                            search: searchSlot,
                            cart: <CartIcon fontColor={cartIconColor} onOpenPanel={openCartInPanel ? () => setCartPanelOpen(true) : undefined} />,
                            account: <LoginPopover {...accountPersonaProps} />,
                        },
                    }}
                />
            </nav>
        );
        return (
            <>
                <ScrollToTop/>
                {/* Notification bars turn the grid header into the same positioned stack the search-row
                    path uses: the wrapper owns the positioning (#navbar's CSS `position: absolute`, or
                    fixed when sticky) and the <nav> goes static inside it, so the bars sit ABOVE the
                    header rather than behind it. Without bars this path renders exactly as before. */}
                {showNotifications ? (
                    <div
                        ref={navRef}
                        className='sf-header-stack'
                        style={{ position: headerConfig.sticky ? 'fixed' as const : 'absolute' as const, top: 0, left: 0, right: 0, width: '100%', zIndex: 200 }}
                    >
                        <NotificationBars config={props.settings.notificationsConfig} />
                        {layoutNav}
                    </div>
                ) : layoutNav}
                {openCartInPanel && canSeeOrders && <CartPanel open={cartPanelOpen} onOpenChange={setCartPanelOpen} />}
            </>
        );
    }

    // The header bar's contents, shared between the two render paths below.
    const headerBarChildren = (
        <>
            <ScrollToTop/>
            <div className={'header-title'} style={{ display: 'flex', alignItems: 'center', overflowX: 'visible', overflowY: 'visible', minWidth: 0,
                // Breathing room off the viewport edge, mirroring .header-actions' paddingRight
                // (headerConfig.brand can still override — it spreads after this).
                paddingLeft: '0.5em',
                // When the inline links row is present, don't let the logo box grow — the links
                // container (.header-center) owns the middle space so left/center/right alignment
                // is measured across the whole gap between the logo and the actions. With no links,
                // the logo box grows as before to push the actions to the right edge.
                flex: (!isNarrow && headerLinks.length > 0) ? '0 1 auto' : '1 1 0', ...headerBrandStyle }}>
                {/* brandInline relocates this content into .header-center below; left empty here
                    (rather than unmounting the div) so the resize-measurement querySelector for
                    '.header-title' below still finds it and the stack/unstack logic keeps working. */}
                {!brandInline && renderBrand()}
            </div>

            {!isNarrow && headerLinks.length > 0 &&
                <div className='header-center' style={{ ...elementStyleFrom(headerLinksConfig.container), justifyContent: headerLinksJustify(headerLinksConfig.align) }}>
                    {brandInline &&
                        <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', ...headerBrandStyle }}>
                            {renderBrand()}
                        </div>
                    }
                    {headerLinks.map(renderHeaderLink)}
                </div>
            }

            <div className='header-actions' style={{textAlign: 'right', paddingRight: '0.5em', display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
                {searchInline &&
                    <div className='home-search-bar'>
                        <SearchBox
                            style={{ margin: '0 auto', width: '100%', ...headerSearchStyle, ...headerSearchVars }}
                            placeholder={L('localization_nav_search_placeholder')}
                            value={keywords}
                            onValueChange={setKeywords}
                            onSearch={searchFor}
                            suggestionsAvailable={props.settings.enableSearchAutocomplete}
                            autocomplete={headerAutocomplete}
                        />
                    </div>
                }
                {/* Portal slot for the search page's relocated controls (filter toggle + paginator) as a
                    single group, when it hides its own search bar on a skinny layout (see SearchPage
                    hidePageSearchBar). Empty otherwise. It sits at the left of the actions row and, because
                    header-actions wraps, the whole group flexes down to a new row together when it doesn't
                    fit — never splitting the filter toggle from the paginator. */}
                <div id='sf-search-header-controls' className='sf-search-header-controls' />
                {/* AC-970 — right-side action links (e.g. Support + Contact CTA), just left of the
                    cart/account group. On narrow they fold into the collapsed header-links menu. */}
                {!isNarrow && actionLinks.length > 0 &&
                    <div className='header-action-links' style={{ display: 'flex', alignItems: 'center', gap: '0.75em', marginRight: '0.75em' }}>
                        {actionLinks.map(renderHeaderLink)}
                    </div>
                }
                {/* Account controls grouped so Menu / Cart / Persona flex down together as a unit rather
                    than breaking apart across rows when the header wraps. */}
                <div className='header-account-group'>
                {isNarrow && headerLinks.length > 0 && !showAutoMenu &&
                    <div style={{paddingLeft: '0.5em'}}>
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <MenuButton className={menuButtonConfigured ? 'sf-menu-btn-configured' : (isDark ? 'sf-menu-btn-dark' : 'sf-menu-btn-light')} appearance={"subtle"} size="large" style={headerMenuButtonStyle}>
                                    {L('localization_nav_more_button', 'More')}
                                </MenuButton>
                            </MenuTrigger>
                            <MenuPopover className='sf-menu-popover' style={{ backgroundColor: menuBg, border: menuBorderDefault, ...headerMenuStyle }}>
                                <MenuList>
                                    {headerLinks.map(renderHeaderLinkMenuItem)}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                }
                {showAutoMenu &&
                    <div style={{paddingLeft: '0.5em'}}>
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <MenuButton className={menuButtonConfigured ? 'sf-menu-btn-configured' : (isDark ? 'sf-menu-btn-dark' : 'sf-menu-btn-light')} appearance={"subtle"} size="large" style={headerMenuButtonStyle}>{L('localization_nav_menu_button')}</MenuButton>
                            </MenuTrigger>
                            <MenuPopover className='sf-menu-popover' style={{ backgroundColor: menuBg, border: menuBorderDefault, ...headerMenuStyle }}>
                                <MenuList>
                                    {/* In the skinny layout the custom header links fold into this menu
                                        instead of getting their own "More" button. */}
                                    {isNarrow && headerLinks.length > 0 &&
                                        <React.Fragment key='collapsed-header-links'>
                                            <MenuGroup>
                                                {headerLinks.map(renderHeaderLinkMenuItem)}
                                            </MenuGroup>
                                            <MenuDivider />
                                        </React.Fragment>
                                    }
                                    {headerLinkChunks.map((group, i) => {
                                        return(
                                            <React.Fragment key={'header-link-group-' + i}>
                                                <MenuGroup>
                                                    {group.map((item, idx) => {
                                                        return getLinkElement(item, i*100+idx);
                                                    })}
                                                </MenuGroup>
                                                {i < headerLinkChunks.length - 1 && <MenuDivider />}
                                            </React.Fragment>
                                        )
                                    })}

                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                }
                {canSeeOrders ?
                <React.Fragment>
                    <div>
                        <CartIcon fontColor={cartIconColor} onOpenPanel={openCartInPanel ? () => setCartPanelOpen(true) : undefined} />
                    </div>
 
                    <div>
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                {/* Signed-in trigger. Takes the same account* parts as the signed-out
                                    persona so the control reads as one thing in both states. `name`
                                    always stays set — it is what Persona derives the avatar initials
                                    from — and only the primary LINE is replaced when styled. */}
                                <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...elementStyleFrom(headerConfig.account) }}>
                                    <Persona
                                        primaryText={accountNameCss ? <span style={accountNameCss}>{name}</span> : ''}
                                        name={name}
                                        avatar={{ style: elementStyleFrom(headerConfig.accountAvatar) }}
                                        style={{cursor:'pointer'}}
                                    />
                                </div>
                            </MenuTrigger>
                            {/* The account menu is header chrome like the category / More menus above, so it
                                takes the same configured-or-luminance-aware surface rather than Fluent's
                                default white — which read as a bright slab hanging off a dark header. */}
                            <MenuPopover className='sf-menu-popover' style={{ backgroundColor: menuBg, border: menuBorderDefault, ...headerMenuStyle }}>
                                <MenuList>
                                    <MenuItem className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle }} onClick={goToAccount}>{L('localization_nav_orders')}</MenuItem>
                                    <MenuItem className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle }} onClick={() => nav('/carts')}>{L('localization_nav_carts')}</MenuItem>
                                    {/* Favorites are just a filtered search, so this is a link rather than a page. */}
                                    {favoritesEnabled && (
                                        <MenuItem className='hc-menu-item' style={{ color: menuItemColor, ...headerMenuItemStyle }} onClick={() => nav('/search?favorites=1')}>{L('localization_nav_favorites')}</MenuItem>
                                    )}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                </React.Fragment> :
                <LoginPopover {...accountPersonaProps} />
                }
                </div>
            </div>
        </>
    );

    // AC-970 — search-as-a-row, and the notification bars above the header. Either one turns the header
    // into a positioned STACK: the wrapper takes over the positioning the bare <nav> normally gets from
    // CSS (#navbar is position:absolute so the header floats over #content, which offsets by
    // --sf-navbar-height); the <nav> goes static inside it and the extra rows sit around it instead of
    // collapsing to the top of the flow behind the absolute nav. navRef moves to the wrapper so the
    // published height covers the whole stack — which is exactly what makes a notification bar push the
    // header and the page down, and let them spring back when it's dismissed. With neither feature on we
    // render the bare <nav> exactly as before — byte-identical for every existing store (enforced by
    // headerFooterGolden).
    const stackedSearchRow = !!props.settings.useHeaderSearch && searchRow;
    if (stackedSearchRow || showNotifications) {
        return (
            <>
            <div
                ref={navRef}
                className='sf-header-stack'
                style={{ position: headerConfig.sticky ? 'fixed' as const : 'absolute' as const, top: 0, left: 0, right: 0, width: '100%', zIndex: 200 }}
            >
                {showNotifications && <NotificationBars config={props.settings.notificationsConfig} />}
                <nav id='navbar' ref={barRef} className={linksStacked ? 'navbar-stacked' : undefined} style={{position: 'static' as const, backgroundColor: props.settings.menuBarColor, color: props.settings.menuBarTextColor, fontFamily: props.settings.headerFont, ...headerBarStyle, ...underHeaderHeroStyle}}>
                    {headerBarChildren}
                </nav>
                {/* The full-width search row. When it's set to stick independently of a non-sticky header,
                    it flips to fixed once scrolled past the bar; the spacer keeps the stack's height stable
                    so --sf-navbar-height (→ #content offset) doesn't jump. */}
                {stackedSearchRow && (
                <div
                    ref={searchRowRef}
                    className='sf-search-row'
                    style={{ backgroundColor: props.settings.menuBarColor, color: props.settings.menuBarTextColor, borderTop: '1px solid rgba(0,0,0,0.06)', padding: '0.6em 1em', boxSizing: 'border-box', ...elementStyleFrom(headerConfig.searchRowStyle), ...(rowPinned ? { position: 'fixed' as const, top: 0, left: 0, right: 0, zIndex: 190 } : {}) }}
                >
                    <div className='home-search-bar' style={{ maxWidth: props.settings.maxWidth || 1240, margin: '0 auto', width: '100%' }}>
                        <SearchBox
                            style={{ width: '100%', ...headerSearchStyle, ...headerSearchVars }}
                            placeholder={L('localization_nav_search_placeholder')}
                            value={keywords}
                            onValueChange={setKeywords}
                            onSearch={searchFor}
                            suggestionsAvailable={props.settings.enableSearchAutocomplete}
                            autocomplete={headerAutocomplete}
                        />
                    </div>
                </div>
                )}
                {rowPinned && <div aria-hidden style={{ height: rowHeight }} />}
            </div>
            {openCartInPanel && canSeeOrders && <CartPanel open={cartPanelOpen} onOpenChange={setCartPanelOpen} />}
            </>
        );
    }

    return (
        <>
        <nav id='navbar' ref={navRef} className={linksStacked ? 'navbar-stacked' : undefined} style={{backgroundColor: props.settings.menuBarColor, color: props.settings.menuBarTextColor, fontFamily: props.settings.headerFont, ...headerBarStyle, ...(headerConfig.sticky ? { position: 'fixed' as const, zIndex: 200 } : {}), ...underHeaderHeroStyle}}>
            {headerBarChildren}
        </nav>
        {openCartInPanel && canSeeOrders && <CartPanel open={cartPanelOpen} onOpenChange={setCartPanelOpen} />}
        </>
    )
}

export default NavBar;